import type { WebSocketTicketResponse } from '@/api/types';
import {
  NOOP_TELEMETRY_REPORTER,
  reportTelemetrySafely,
  type TelemetryReporter,
} from '@/telemetry';

export type UserDirectoryEntry = {
  connectionId: string;
  userId: string;
  clientInstanceId: string;
  displayName: string;
  connectedAt: string;
  viewerCount?: number;
};

export type UserViewerCount = { connectionId: string; viewerCount: number };

export type UserFlowSignal =
  | { type: 'offer'; sdp: string }
  | { type: 'answer'; sdp: string }
  | {
      type: 'iceCandidate';
      candidate: string;
      sdpMid?: string | null;
      sdpMLineIndex?: number | null;
      usernameFragment?: string | null;
    }
  | { type: 'close' }
  | { type: 'requestSnapshot' }
  | { type: 'snapshotChunk'; transferId: string; index: number; total: number; payload: string };

export type DirectoryEvent = {
  type: 'directorySnapshot' | 'robotStatus' | 'prepareReady' | 'prepareFailed' | 'userDirectorySnapshot' | 'userStatus' | 'userViewerCounts' | 'userFlowSignal' | 'sessionReplaced' | 'error';
  robotId?: string;
  online?: boolean;
  connectionRequestId?: string;
  generation?: number;
  occurredAt?: string;
  users?: UserDirectoryEntry[];
  user?: UserDirectoryEntry;
  counts?: UserViewerCount[];
  sourceConnectionId?: string;
  sourceUserId?: string;
  sourceDisplayName?: string;
  sessionId?: string;
  signal?: UserFlowSignal;
  message?: string;
  [key: string]: unknown;
};

type TicketProvider = () => Promise<WebSocketTicketResponse>;
type Listener = (event: DirectoryEvent) => void;
type OperatorPresence = {
  clientInstanceId: string;
  displayName: string;
  flowSharing: boolean;
};
export type RobotDirectoryConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed';
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 60_000;

export const directoryReconnectDelay = (attempt: number) => Math.min(
  INITIAL_RECONNECT_DELAY_MS * 2 ** Math.max(0, attempt),
  MAX_RECONNECT_DELAY_MS,
);

export const validateWebSocketTicket = (
  value: WebSocketTicketResponse,
  now = Date.now(),
): WebSocketTicketResponse => {
  if (!value.ticket || typeof value.ticket !== 'string') throw new Error('Directory ticket is missing');
  let url: URL;
  try {
    url = new URL(value.websocketUrl);
  } catch {
    throw new Error('Directory WebSocket URL is invalid');
  }
  if (url.protocol !== 'wss:') throw new Error('Directory WebSocket URL must use WSS');
  const expiresAt = Date.parse(value.expiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= now) throw new Error('Directory ticket has expired');
  return value;
};

export class RobotDirectoryClient {
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private reconnectStartedAt: number | null = null;
  private connecting = false;
  private disposed = false;
  private listeners = new Set<Listener>();
  private stateListeners = new Set<(state: RobotDirectoryConnectionState) => void>();
  private state: RobotDirectoryConnectionState = 'idle';

  constructor(
    private readonly ticketProvider: TicketProvider,
    private readonly telemetryReporter: TelemetryReporter = NOOP_TELEMETRY_REPORTER,
    private operatorPresence?: OperatorPresence,
  ) {}

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  subscribeState(listener: (state: RobotDirectoryConnectionState) => void) {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => { this.stateListeners.delete(listener); };
  }

  getState() { return this.state; }

  updateOperatorPresence(presence: OperatorPresence): boolean {
    this.operatorPresence = presence;
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    try {
      this.sendJoin(this.socket);
      return true;
    } catch (error) {
      this.failSocket(this.socket, error);
      return false;
    }
  }

  async connect(): Promise<void> {
    if (
      this.disposed
      || this.connecting
      || this.socket?.readyState === WebSocket.OPEN
      || this.socket?.readyState === WebSocket.CONNECTING
    ) return;
    this.connecting = true;
    this.setState(this.reconnectStartedAt === null ? 'connecting' : 'reconnecting');
    try {
      const ticket = validateWebSocketTicket(await this.ticketProvider());
      if (this.disposed) return;
      const separator = ticket.websocketUrl.includes('?') ? '&' : '?';
      const socket = new WebSocket(`${ticket.websocketUrl}${separator}ticket=${encodeURIComponent(ticket.ticket)}`);
      this.socket = socket;
      socket.onopen = () => {
        if (this.socket !== socket) return;
        this.setState('connected');
        if (this.reconnectStartedAt !== null) {
          reportTelemetrySafely(this.telemetryReporter, {
            eventType: 'websocket.reconnected',
            durationMs: performance.now() - this.reconnectStartedAt,
          });
          this.reconnectStartedAt = null;
        }
        this.reconnectAttempt = 0;
        try {
          this.sendJoin(socket);
        } catch (error) {
          this.failSocket(socket, error);
        }

      };
      socket.onmessage = (message) => {
        if (this.socket !== socket) return;
        try {
          const event = JSON.parse(String(message.data)) as DirectoryEvent;
          this.listeners.forEach((listener) => listener(event));
          if (event.type === 'sessionReplaced') {
            this.stopReconnecting(socket);
          }
        } catch {
          this.emitError('Directory event parse failed');
        }
      };
      socket.onerror = () => {
        if (this.socket === socket) this.failSocket(socket, new Error('Directory WebSocket error'));
      };
      socket.onclose = () => {
        if (this.socket !== socket) return;
        this.socket = null;
        this.setState('reconnecting');
        this.scheduleReconnect();
      };
    } catch (error) {
      this.emitError(error instanceof Error ? error.message : String(error));
      this.setState('failed');
      this.scheduleReconnect();
    } finally {
      this.connecting = false;
    }
  }

  prepare(sessionId: string, robotIds: string[], connectionRequestId: string, generation: number): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    try {
      this.socket.send(JSON.stringify({ action: 'prepare', sessionId, robotIds, connectionRequestId, generation }));
      return true;
    } catch (error) {
      this.failSocket(this.socket, error);
      return false;
    }
  }

  sendUserFlowSignal(
    targetConnectionId: string,
    sessionId: string,
    signal: UserFlowSignal,
  ): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    try {
      this.socket.send(JSON.stringify({
        action: 'userFlowSignal',
        targetConnectionId,
        sessionId,
        signal,
      }));
      return true;
    } catch (error) {
      this.failSocket(this.socket, error);
      return false;
    }
  }

  setUserFlowViewing(targetConnectionId: string | null): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    try {
      this.socket.send(JSON.stringify({ action: 'userFlowViewing', targetConnectionId }));
      return true;
    } catch (error) {
      this.failSocket(this.socket, error);
      return false;
    }
  }

  refreshUserDirectory(): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    try {
      this.socket.send(JSON.stringify({ action: 'refreshUserDirectory' }));
      return true;
    } catch (error) {
      this.failSocket(this.socket, error);
      return false;
    }
  }

  private sendJoin(socket: WebSocket) {
    socket.send(JSON.stringify({ action: 'join', ...this.operatorPresence }));
  }

  private failSocket(socket: WebSocket, error: unknown) {
    if (this.socket !== socket) return;
    this.emitError(error instanceof Error ? error.message : String(error));
    this.setState('failed');
    this.socket = null;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    try { socket.close(); } catch { /* reconnect still continues */ }
    this.scheduleReconnect();
  }

  private stopReconnecting(socket: WebSocket) {
    if (this.socket !== socket) return;
    this.disposed = true;
    this.socket = null;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    this.setState('idle');
    try { socket.close(4001, 'session replaced'); } catch { /* session is already closed */ }
  }

  private emitError(message: string) {
    this.listeners.forEach((listener) => listener({ type: 'error', message }));
  }

  private setState(state: RobotDirectoryConnectionState) {
    if (this.state === state) return;
    this.state = state;
    this.stateListeners.forEach((listener) => listener(state));
  }

  private scheduleReconnect() {
    if (this.disposed || this.reconnectTimer !== null) return;
    this.reconnectStartedAt ??= performance.now();
    const delay = directoryReconnectDelay(this.reconnectAttempt);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect().catch(() => undefined);
    }, delay);
  }

  dispose() {
    this.disposed = true;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.reconnectStartedAt = null;
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) socket.close(1000, 'dispose');
    }
    this.listeners.clear();
    this.setState('idle');
    this.stateListeners.clear();
  }
}
